/**
 * AdminMedia — paste the public URL for the hero demo video.
 *
 * No file upload here on purpose: the founder hosts the clip wherever
 * they like (Cloudflare R2, Vimeo MP4 direct, Mux, S3). We just store
 * the URL string and the landing page renders a <video> tag pointing at
 * it. Clearing the field hides the player on the landing page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

export default function AdminMedia() {
  const [url, setUrl] = useState("");
  const [original, setOriginal] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/site/demo-video").then((r) => {
      const v = r.data?.url || "";
      setUrl(v);
      setOriginal(v);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setError("");
    setSavedMsg("");
    const next = url.trim();
    if (next && !next.toLowerCase().startsWith("https://")) {
      setError("URL must start with https://");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/site/demo-video", { url: next || null });
      setOriginal(next);
      setSavedMsg(next ? "Saved. Landing page will load this clip." : "Cleared. Landing page now shows the placeholder.");
      setTimeout(() => setSavedMsg(""), 2400);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] px-4 sm:px-6 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/pick-board" className="pill-btn" data-testid="admin-media-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <PlayCircle size={22} aria-hidden /> Landing media
          </h1>
        </div>
        <AdminTabs current="media" />
        <section className="brutal-card bg-white p-5 mt-4" data-testid="admin-media-card">
          <h2 className="text-lg font-extrabold mb-2">Hero demo video URL</h2>
          <p className="text-sm text-[color:var(--ink)]/70 mb-3">
            Paste a direct https:// link to an .mp4 (or .webm) clip. Anything
            you can host on Cloudflare R2, S3, Mux, or Vimeo's direct-MP4
            export works. Clear the field to hide the player on the landing
            page (we'll show the "Demo coming soon" placeholder instead).
          </p>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://cdn.example.com/sonspeak-demo.mp4"
            className="brutal-input w-full"
            maxLength={600}
            data-testid="admin-media-url-input"
          />
          {error && (
            <p className="text-sm text-red-600 mt-2" data-testid="admin-media-error">{error}</p>
          )}
          {savedMsg && (
            <p className="text-sm text-emerald-700 font-bold mt-2" data-testid="admin-media-saved">{savedMsg}</p>
          )}
          <div className="flex items-center gap-2 justify-end mt-3">
            <button
              type="button"
              onClick={save}
              disabled={busy || url.trim() === original.trim()}
              className="pill-btn primary"
              data-testid="admin-media-save"
            >
              <Save size={14} /> {busy ? "Saving…" : "Save"}
            </button>
          </div>

          {url && url.trim().toLowerCase().startsWith("https://") && (
            <div className="mt-5">
              <h3 className="text-sm font-bold mb-2">Live preview</h3>
              <video
                src={url}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-lg border-2 border-[color:var(--ink)]"
                data-testid="admin-media-preview"
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
